import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { connect } from 'react-redux'
import moment from 'moment';
import { DataTable, Badge, Button, Icon, LoadingOverlay, Spinner } from './ui'
import DateFilter from './DateFilter'
import MonitorToolbar from './MonitorToolbar'
import EventDrawer from './EventDrawer'
import { setEventState, setEventPagination, getEvents } from '../slices'

// GateType.sName -> Badge variant (in/out/denied/accident/auth). LOGIN/LOGOUT -> auth.
const TYPE_VARIANT = { IN: 'in', OUT: 'out', DENIED: 'denied', ACCIDENT: 'accident', LOGIN: 'auth', LOGOUT: 'auth' }

/*
 * [86bbumedw] P4 — PaxCell: the Pax-count cell. When the crossing's site captures passenger NAMES
 * (backend attaches `event.passengers`), the count is a button → a click popover of the names.
 * Count-only sites (no `passengers`) render the plain number. The popover portals to <body> and is
 * anchored to the button so the scrolling event table can't clip it; it closes on outside-click,
 * Esc, scroll, or resize. stopPropagation keeps the click off the row (which opens the drawer).
 */
function PaxCell({ row }) {
  const [pos, setPos] = useState(null); // {top,left} of the anchor, or null when closed
  const count = (row.bPassengerCount && parseInt(row.bPassengerCount, 10) > 0) ? row.bPassengerCount : null;
  const names = Array.isArray(row.passengers) ? row.passengers : [];
  useEffect(() => {
    if (!pos) return;
    const close = () => setPos(null);
    const onEsc = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', close);
    document.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      document.removeEventListener('keydown', onEsc);
    };
  }, [pos]);
  if (!count) return <span>—</span>;
  if (names.length === 0) return <span>{count}</span>; // count-only site
  const toggle = (e) => {
    e.stopPropagation();
    if (pos) { setPos(null); return; }
    const r = e.currentTarget.getBoundingClientRect();
    setPos({ top: r.bottom + 4, left: r.right });
  };
  return (
    <>
      <button type="button" className="paxcell__btn" onClick={toggle} title="Show passenger names" aria-expanded={!!pos}>
        {count}
      </button>
      {pos && createPortal(
        <div className="paxcell__pop" style={{ top: pos.top, left: pos.left }} onMouseDown={(e) => e.stopPropagation()}>
          <div className="paxcell__poptitle">Passengers ({names.length})</div>
          <ul className="paxcell__list">
            {names.map((p, i) => <li key={i}>{`${p.sFirstName || ''} ${p.sLastName || ''}`.trim() || '—'}</li>)}
          </ul>
        </div>,
        document.body
      )}
    </>
  );
}

/*
 * EventsTable — Console reskin of the live event feed.
 *
 * PRESENTATION ONLY. Every filter/sort/page change round-trips through the same
 * Redux path the previous @tanstack table used:
 *   setEventState({field:'eventFilters'|'eventSorting'|'eventListReload'|'eventListLoading'})
 *   + setEventPagination(...)  ->  the reload useEffect fires getEvents({filters}).
 * The 3-min incremental poll, poll-abort (filtered/sorted/paged), lastTs merge,
 * and additive eventCount all live in EventSlice/App and are untouched.
 */
const EventsTable = (props) => {
  const {
    events,
    eventCount,
    eventFilters,
    eventSorting,
    eventPagination,
    eventListReload,
    eventListLoading,
    eventListLoadingMore,
    eventHasMore,
    eventPage,
    eventDownloadStatus,
    pollHealth,
    gates,
    sites,
    customers,
    guards,
    subscribers,
    accountType,
    authId,
  } = props

  // manual mount-guard: skip the reload effect's first (mount) run so it does
  // not double-fetch — App.getInitialData already does the initial page-1 load.
  // The reload effect fires on every real filter/sort/page change thereafter.
  // (The old table used a >=2 threshold because a second mount effect — the
  // header-sort effect, removed in this reskin — also bumped this ref; with a
  // single incrementing effect the mount is one tick, so the threshold is >=1.)
  const useEffectRan = useRef(0)

  // local text input for the search box. The backend's gateevent.list supports
  // per-column LIKE filters (driver / company / lpn) but NOT a combined search
  // param, so — to change no query logic and invent no param — this box drives
  // the existing `driver` LIKE filter (the most common lookup).
  const committedSearch = (eventFilters.find(f => f.id === 'driver')?.value) || ''
  const [ searchText, setSearchText ] = useState(committedSearch)

  // selected event for the detail drawer (presentational only; no new fetch)
  const [ selected, setSelected ] = useState(null)

  const pageSize = eventPagination.pageSize || 15

  // keep the local search box in sync when Redux clears it underneath us (Reset List).
  useEffect(() => { setSearchText(committedSearch) }, [committedSearch])

  // Build the request params from the committed filters + sort. The `timestamp` filter
  // carries the date range as {sDate,eDate}; unpack it to the backend's keys. `page` picks
  // the slice (infinite scroll appends successive pages; a filter/sort change replaces).
  const buildParams = (page) => {
    let params = Object.fromEntries(eventFilters.map(obj => [obj.id, obj.value]))
    if (params.timestamp) {
      params.sDate = params.timestamp.sDate
      params.eDate = params.timestamp.eDate
      delete params.timestamp
    }
    if (eventSorting && eventSorting[0]) {
      params.orderBy = eventSorting[0].id
      params.orderDir = eventSorting[0].desc ? 'desc' : 'asc'
    }
    params.page = page
    return params
  }

  // Reload from page 1 (REPLACE) on any filter / sort / reset change. App.getInitialData
  // does the initial page-1 load, so skip the first (mount) run to avoid a double-fetch.
  useEffect(() => {
    if (useEffectRan.current >= 1) {
      props.setEventState({ field: 'eventListLoading', value: true })
      props.getEvents({ filters: buildParams(1) })
    } else {
      useEffectRan.current++
    }
  // eslint-disable-next-line
  }, [eventListReload])

  // Infinite scroll: append the next page when the table is scrolled near its bottom.
  const loadMore = () => {
    if (eventListLoading || eventListLoadingMore || !eventHasMore) return
    props.setEventState({ field: 'eventListLoadingMore', value: true })
    props.getEvents({ filters: buildParams(eventPage + 1), append: true })
  }

  // ---- Reset List button from the toolbar clears local input state ----
  useEffect(() => {
    if (props.reset) {
      setSearchText('')
      props.toggleReset()
    }
  // eslint-disable-next-line
  }, [props.reset])

  // ---- filter helpers: all commit through the SAME actions as the old table ----

  // Type chip filter: sets/clears the typeId filter, resets to page 1, triggers reload.
  const setTypeFilter = (value) => {
    if (eventListLoading) return
    // mirror the old onFilterChange: header.column.setFilterValue via setEventFilters,
    // but the value flows through setEventState('eventFilters') the same way runInputFilterSearch does.
    const others = eventFilters.filter(f => f.id !== 'typeId')
    const next = value ? [ ...others, { id: 'typeId', value } ] : others
    props.setEventState({ field: 'eventFilters', value: next })
    props.setEventPagination({ pageIndex: 0, pageSize })
    props.setEventState({ field: 'eventListReload', value: eventListReload + 1 })
  }

  const activeType = eventFilters.find(f => f.id === 'typeId')?.value || ''

  // Gate / Customer / Logged-By select filters (same three the old header had).
  const setSelectFilter = (id, value) => {
    if (eventListLoading) return
    const others = eventFilters.filter(f => f.id !== id)
    const next = value ? [ ...others, { id, value } ] : others
    props.setEventState({ field: 'eventFilters', value: next })
    props.setEventPagination({ pageIndex: 0, pageSize })
    props.setEventState({ field: 'eventListReload', value: eventListReload + 1 })
  }
  const selectValue = (id) => eventFilters.find(f => f.id === id)?.value || ''

  // Search box: commits the driver query on Enter (matches the old "search on
  // Enter" behavior for the header text filters). Maps to the real `driver`
  // LIKE key the backend already understands.
  const commitSearch = () => {
    if (eventListLoading) return
    const others = eventFilters.filter(f => f.id !== 'driver')
    const next = searchText ? [ ...others, { id: 'driver', value: searchText } ] : others
    props.setEventState({ field: 'eventFilters', value: next })
    props.setEventPagination({ pageIndex: 0, pageSize })
    props.setEventState({ field: 'eventListReload', value: eventListReload + 1 })
  }

  // Clear button: drops just the search box + its committed driver filter (Reset
  // List, in contrast, clears every filter + pagination). Commits the removal so
  // the feed refreshes immediately.
  const clearSearch = () => {
    if (eventListLoading) return
    setSearchText('')
    const others = eventFilters.filter(f => f.id !== 'driver')
    props.setEventState({ field: 'eventFilters', value: others })
    props.setEventPagination({ pageIndex: 0, pageSize })
    props.setEventState({ field: 'eventListReload', value: eventListReload + 1 })
  }

  // ---- column sort: commit through eventSorting; the reload effect refetches (orderBy/orderDir) ----
  const commitSort = (key) => {
    if (eventListLoading) return
    const cur = eventSorting && eventSorting[0]
    const next = (cur && cur.id === key) ? [{ id: key, desc: !cur.desc }] : [{ id: key, desc: true }]
    props.setEventState({ field: 'eventSorting', value: next })
    props.setEventPagination({ pageIndex: 0, pageSize })
    props.setEventState({ field: 'eventListReload', value: eventListReload + 1 })
  }
  const sortKey = (eventSorting && eventSorting[0]) ? eventSorting[0].id : 'timestamp'
  const sortDir = (eventSorting && eventSorting[0]) ? (eventSorting[0].desc ? 'desc' : 'asc') : 'desc'

  // ---- date filter: committed via the DateFilter modal's Apply (not on each keystroke),
  // so a range doesn't fire until both ends are chosen. Value rides `timestamp` as {sDate,eDate}. ----
  const dateVal = eventFilters.find(f => f.id === 'timestamp')?.value || {}
  const applyDates = (range) => {
    if (eventListLoading) return
    const others = eventFilters.filter(f => f.id !== 'timestamp')
    props.setEventState({ field: 'eventFilters', value: [ ...others, { id: 'timestamp', value: range } ] })
    props.setEventPagination({ pageIndex: 0, pageSize })
    props.setEventState({ field: 'eventListReload', value: eventListReload + 1 })
  }
  const clearDates = () => {
    if (eventListLoading) return
    props.setEventState({ field: 'eventFilters', value: eventFilters.filter(f => f.id !== 'timestamp') })
    props.setEventPagination({ pageIndex: 0, pageSize })
    props.setEventState({ field: 'eventListReload', value: eventListReload + 1 })
  }

  // [86bbwu3px item 4] Manual Refresh — force a genuine page-1 fetch KEEPING the current filters
  // (a fresh load, no lastId watermark), so an admin can pull the latest crossings on demand even
  // while filtered/sorted/paged (when live polling is paused — item 14). Distinct from Reset, which
  // clears every filter. Uses the same replace-from-page-1 path as a filter change.
  const refresh = () => {
    if (eventListLoading) return
    props.setEventState({ field: 'eventListLoading', value: true })
    props.getEvents({ filters: buildParams(1) })
  }

  const shown = events.length

  // Reset List shows only when something is actually filtered/sorted/paged
  // (same predicate the monitor used before Export/Reset moved into this toolbar).
  const showReset = eventFilters.length > 0 || (eventSorting && eventSorting.length > 0) || eventPagination.pageIndex > 0

  // ---- columns (same data mapping + row source as before, badge for Type) ----
  // explicit per-column widths — auto-layout honors these as minimums so no
  // column squishes below its content (the Company↔Driver collision). Their sum
  // (~1074) is the table min-width; below that the .table-wrap scrolls instead.
  const columns = [
    {
      key: 'timestamp',
      header: 'Date / Time',
      className: 'ts',
      width: 100,
      sortable: true,
      render: (row) => (
        <div className="cellstack">
          <span className="mono">{ moment(row.timestamp).format('hh:mm a') }</span>
          <small>{ moment(row.timestamp).format('MM/DD/YY') }</small>
        </div>
      ),
    },
    {
      key: 'typeId',
      header: 'Type',
      width: 110,
      sortable: true,
      render: (row) => {
        const name = row.GateType?.sName
        return name
          ? <Badge variant={TYPE_VARIANT[name] || 'neutral'}>{ name }</Badge>
          : '—'
      },
    },
    { key: 'gateId', header: 'Gate', width: 116, sortable: true, render: (row) => { const v = row.Gate?.sName || 'UNASSIGNED'; return <span title={v}>{v}</span> } },
    {
      key: 'siteId', header: 'Site', width: 124,
      render: (row) => { const v = row.Gate?.GateSite?.sName || '—'; return <span className="sub" title={v} style={{ color: 'var(--text-2)' }}>{v}</span> },
    },
    {
      key: 'customerId', header: 'Customer', width: 106, sortable: true,
      render: (row) => { const v = row.GateCustomer?.sName || 'UNASSIGNED'; return <span className="sub" title={v} style={{ color: 'var(--text-2)' }}>{v}</span> },
    },
    { key: 'company', header: 'Company', width: 126, sortable: true, render: (row) => { const v = row.GateCompany?.sName || '—'; return <span title={v}>{v}</span> } },
    { key: 'driver', header: 'Driver', width: 120, sortable: true, render: (row) => { const v = row.GatePerson?.sFullName || '—'; return <span title={v}>{v}</span> } },
    { key: 'lpn', header: 'Plate', mono: true, width: 96, sortable: true, render: (row) => row.GateLPN?.sName || '—' },
    {
      key: 'pax', header: 'Pax', num: true, width: 54,
      render: (row) => <PaxCell row={row} />,
    },
    {
      key: 'guardId', header: 'Logged By', width: 110, sortable: true,
      render: (row) => (
        <span className="sub" style={{ color: 'var(--text-2)', textTransform: 'capitalize' }}>
          { row.GateUser?.sFirstName?.slice(0, 1) }. { row.GateUser?.sLastName }
        </span>
      ),
    },
  ]

  // real crossings (GateType.id < 100) open the drawer; LOGIN/LOGOUT rows do not.
  const onRowClick = (row) => {
    if (row.GateType && row.GateType.id < 100) setSelected(row)
  }

  return (
    <>
      <div className="tablecard">
        {/* 2-row toolbar (shared with User Log via MonitorToolbar):
            row 1 = search + type chips (Export pinned far right);
            row 2 = date + customer/site/gate/guard selects (live-paused + Reset far right). */}
        <MonitorToolbar
          search={
            /* search: driver name — commits on Enter or the Search button; Clear
               drops just this filter (Reset List clears everything). */
            <div className="searchgroup">
              <div className={`search${searchText ? ' search--clearable' : ''}`}>
                <Icon name="search" size={15} />
                <input
                  className="input"
                  placeholder="Search by driver name…"
                  value={searchText}
                  disabled={eventListLoading}
                  onChange={e => setSearchText(e.target.value)}
                  onKeyUp={e => { if (e.key === 'Enter') commitSearch() }}
                />
                { searchText &&
                  // in-field ✕: clears the box, and drops the committed driver filter if one is live
                  <button
                    type="button"
                    className="search__clear"
                    aria-label="Clear search"
                    title="Clear"
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => (committedSearch ? clearSearch() : setSearchText(''))}
                  >
                    <Icon name="close" size={14} />
                  </button>
                }
              </div>
              <Button size="sm" variant="primary" disabled={eventListLoading} onClick={commitSearch}>Search</Button>
            </div>
          }
          chips={
            /* type-filter chips — crossings only. LOGIN/LOGOUT are session events,
               excluded from this feed (backend gateevent.list guards typeId 100/101);
               they live on the User Log, not here. */
            <div className="chipfilter" role="group" aria-label="Type">
              <button className={activeType === '' ? 'on' : ''} disabled={eventListLoading} onClick={() => setTypeFilter('')}>All</button>
              <button className={activeType === '1' ? 'on' : ''} disabled={eventListLoading} onClick={() => setTypeFilter('1')}>In</button>
              <button className={activeType === '2' ? 'on' : ''} disabled={eventListLoading} onClick={() => setTypeFilter('2')}>Out</button>
              <button className={activeType === '3' ? 'on' : ''} disabled={eventListLoading} onClick={() => setTypeFilter('3')}>Denied</button>
              <button className={activeType === '4' ? 'on' : ''} disabled={eventListLoading} onClick={() => setTypeFilter('4')}>Accident</button>
            </div>
          }
          actionsTop={
            /* Export acts on the events feed (moved here from the on-site card, where
               Export CSV misleadingly implied on-site data). Pinned to row-1 far right. */
            eventDownloadStatus
              ? <Button size="sm" disabled icon={<Spinner size={14} />}>Downloading…</Button>
              : <Button size="sm" icon={<Icon name="download" size={15} />} disabled={eventListLoading} onClick={props.toggleDownloadModal}>Export CSV</Button>
          }
          filters={
            /* feed filters (independent; the backend ANDs them). date ▸ then broad→narrow
               location: subscriber ▸ customer ▸ site ▸ gate ▸ guard. Subscriber is Super-only
               (the backend honors a client subscriberId only for unscoped/super callers — every
               other role is force-scoped to their own subscriber); customer is hidden for 'direct'. */
            <>
              <DateFilter value={dateVal} onApply={applyDates} onClear={clearDates} disabled={eventListLoading} label="date" />
              { authId === 100 &&
                <select className="select select--sm" value={selectValue('subscriberId')} disabled={eventListLoading} onChange={e => setSelectFilter('subscriberId', e.target.value)}>
                  <option value="">All subscribers</option>
                  { subscribers.map(s => <option key={s.id} value={s.id}>{ s.sName }</option>) }
                </select>
              }
              { accountType !== 'direct' &&
                <select className="select select--sm" value={selectValue('customerId')} disabled={eventListLoading} onChange={e => setSelectFilter('customerId', e.target.value)}>
                  <option value="">All customers</option>
                  { customers.map(c => <option key={c.id} value={c.id}>{ c.sName }</option>) }
                </select>
              }
              <select className="select select--sm" value={selectValue('siteId')} disabled={eventListLoading} onChange={e => setSelectFilter('siteId', e.target.value)}>
                <option value="">All sites</option>
                { sites.map(s => <option key={s.id} value={s.id}>{ s.sName }</option>) }
              </select>
              <select className="select select--sm" value={selectValue('gateId')} disabled={eventListLoading} onChange={e => setSelectFilter('gateId', e.target.value)}>
                <option value="">All gates</option>
                { gates.map(g => <option key={g.id} value={g.id}>{ g.sName }</option>) }
              </select>
              <select className="select select--sm" value={selectValue('guardId')} disabled={eventListLoading} onChange={e => setSelectFilter('guardId', e.target.value)}>
                <option value="">All guards</option>
                { guards.map(u => <option key={u.id} value={u.id}>{ u.sFullName }</option>) }
              </select>
            </>
          }
          actionsBottom={
            /* [86bbwu3px item 4] always-available Refresh (pull latest, keep filters); the live-
               paused indicator + Reset (clear filters) ride with the filter state on row-2 far right. */
            <>
              { /* [86bbwu3px] broken-poll indicator: when live polling has failed to reach the server,
                   surface it (subtle, not a modal) and pull the eye to Refresh. It clears itself the
                   moment a poll succeeds again — polling also keeps retrying on its own in the
                   background, so Refresh is a "now" button, not the only recovery. */ }
              { pollHealth === 'error' && !showReset &&
                /* only while live polling is actually RUNNING — when filtered/sorted/paged the feed
                   is intentionally paused (the amber "Live paused" chip shows instead), and polls are
                   skipped, so a stale 'error' must not read as "interrupted" there. */
                <span className="pollbroken" role="status" title="Live updates were interrupted — couldn't reach the server. They'll resume automatically; click Refresh to pull the latest right now.">
                  <span className="pollbroken__dot" /> Live updates interrupted
                </span>
              }
              <Button size="sm" variant={pollHealth === 'error' ? 'primary' : 'ghost'} icon={<Icon name="refresh" size={15} />} disabled={eventListLoading} onClick={refresh}
                title="Refresh — pull the latest crossings now (keeps your current filters)">Refresh</Button>
              { showReset &&
                <span className="livepause" title="Live updates pause while filtered, sorted, or paged. Click Refresh to pull the latest (keeps filters), or Reset to clear filters and resume live updates.">
                  <span className="livepause__dot" /> Live paused
                </span>
              }
              { showReset &&
                <Button size="sm" variant="ghost" icon={<Icon name="close" size={15} />} disabled={eventListLoading} onClick={props.toggleReset}>Reset</Button>
              }
            </>
          }
        />

        <LoadingOverlay show={eventListLoading} label="Loading events" />

        <DataTable
          className="tbl--events"
          columns={columns}
          rows={events}
          rowKey={(row) => row.id}
          onRowClick={onRowClick}
          selectedKey={selected?.id}
          sortKey={sortKey}
          sortDir={sortDir}
          onSort={commitSort}
          onScrollEnd={loadMore}
          loadingMore={eventListLoadingMore}
          empty="No Events Found"
        />

        <div className="pagination">
          <div className="rowcount">
            Showing <span className="num">{shown.toLocaleString()}</span> of <span className="num">{ (eventCount || 0).toLocaleString() }</span> events
            { !eventHasMore && shown > 0 && !eventListLoading && !eventListLoadingMore && <span className="sub"> · all loaded</span> }
          </div>
        </div>
      </div>

      <EventDrawer event={selected} onClose={() => setSelected(null)} />
    </>
  )
}

const mapStateToProps = (state) => {
  return {
    events: state.event.events,
    eventCount: state.event.eventCount,
    eventFilters: state.event.eventFilters,
    eventSorting: state.event.eventSorting,
    eventPagination: state.event.eventPagination,
    eventListReload: state.event.eventListReload,
    eventListLoading: state.event.eventListLoading,
    eventListLoadingMore: state.event.eventListLoadingMore || false,
    eventHasMore: state.event.eventHasMore || false,
    eventPage: state.event.eventPage || 1,
    eventDownloadStatus: state.event.eventDownloadStatus,
    pollHealth: state.event.pollHealth || 'ok',
    gates: state.gate.gates,
    sites: state.site.sites || [],
    customers: state.customer.customers,
    guards: state.user.guards,
    subscribers: state.subscriber.subscribers || [],
    accountType: state.auth.accountType,
    authId: state.auth.authId,
  }
}
export default connect(mapStateToProps, { setEventState, setEventPagination, getEvents })(EventsTable)
